import { Injectable } from "@angular/core";
import { Observable, of, timer } from "rxjs";
import { catchError, concatMap, last, map, take, takeUntil, takeWhile } from "rxjs/operators";
import {
  EnvelopeRecipientStatus,
  EnvelopeStatus,
  SigningSessionData,
  SigningStateData,
  SigningSubject,
} from "../models/docusign-signing.model";
import { DocuSignSigningRemote } from "../../infrastructure/docusign-signing.remote";

/** The DocuSign signing ceremony's public API. */
@Injectable({
  providedIn: "root",
})
export class DocuSignSigningFacade {
  /** How often the return page asks whether RYNO has recorded the signature. */
  private static readonly PollInterval = 2000;

  /** How long to keep asking, in wall-clock time. */
  private static readonly PollTimeout = 60000;

  constructor(private readonly remote: DocuSignSigningRemote) {}

  startSigningSession$(subject: SigningSubject): Observable<SigningSessionData> {
    return this.remote.startSigningSession(subject);
  }

  getSigningState$(subject: SigningSubject): Observable<SigningStateData> {
    return this.remote.getSigningState(subject);
  }

  /** Polls until RYNO has recorded this signer's signature, or the timeout is reached. */
  waitForRecordedSignature$(subject: SigningSubject): Observable<SigningStatePollResult> {
    const attempts = Math.ceil(DocuSignSigningFacade.PollTimeout / DocuSignSigningFacade.PollInterval);
    const notRecorded: SigningStatePollResult = { state: null, signatureRecorded: false };

    return timer(0, DocuSignSigningFacade.PollInterval).pipe(
      take(attempts),
      // concatMap, not switchMap: a read slower than the interval must not be cancelled and replaced.
      concatMap((attempt) =>
        this.remote.getSigningState(subject).pipe(
          map((state) => ({
            state: state as SigningStateData | null,
            signatureRecorded: DocuSignSigningFacade.isSignatureRecorded(state),
            isLastAttempt: attempt === attempts - 1,
          })),
          // A failed attempt is "not yet", not the end of the wait.
          catchError(() =>
            of({
              state: null as SigningStateData | null,
              signatureRecorded: false,
              isLastAttempt: attempt === attempts - 1,
            })
          )
        )
      ),
      // Keeps the first recorded result (inclusive) and stops there.
      takeWhile((result) => !result.signatureRecorded && !result.isLastAttempt, true),
      // Placed here so it completes the whole chain, not only the operator it follows.
      takeUntil(timer(DocuSignSigningFacade.PollTimeout)),
      map((result) => ({ state: result.state, signatureRecorded: result.signatureRecorded })),
      // Exactly one emission, always — the last result seen, or the fallback if no read landed.
      last(null, notRecorded)
    );
  }

  /** Whether RYNO — not DocuSign — considers this signer done. */
  private static isSignatureRecorded(state: SigningStateData): boolean {
    return (
      state?.recipientStatus === EnvelopeRecipientStatus.Completed &&
      (state?.signedDocumentCodes?.length ?? 0) > 0
    );
  }

  /** Whether the agreement is fully executed — every signer done, not just the caller. */
  static isExecuted(state: SigningStateData | null): boolean {
    return state?.envelopeStatus === EnvelopeStatus.Completed;
  }
}

export interface SigningStatePollResult {
  /** Null when every attempt failed — the wait ran out without a usable answer. */
  state: SigningStateData | null;
  signatureRecorded: boolean;
}
